import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import confetti from 'canvas-confetti'

function Results({ score }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  return isSubmitting ? (
    <FinalStep score={score} />
  ) : (
    <CollectEmailStep score={score} setIsSubmitting={setIsSubmitting} />
  )
}

const CollectEmailStep = ({ score, setIsSubmitting }) => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/extensions/modules/module/unauth/submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: 'CollectEmailModule',
            data: {
              email: email,
              test_result: score.toString(),
            },
          }),
        },
      )

      if (!response.ok) {
        let errorMessage = `Failed to submit results (${response.status})`
        try {
          const errorText = await response.text()
          if (errorText) {
            errorMessage = errorText
          }
        } catch {
          errorMessage = `Server error: ${response.status} ${response.statusText}`
        }
        throw new Error(errorMessage)
      }

      const contentType = response.headers.get('content-type')
      
      if (contentType && contentType.includes('application/json')) {
        await response.json().catch(() => null)
      } else {
        await response.text().catch(() => null)
      }

      setIsSubmitting(true)
    } catch (error) {
      console.error('Error submitting results:', error)
      setError(error.message || 'Failed to submit. Please try again.')
      setIsLoading(false)
    }
  }

  return (
    <motion.div
      className='bg-gradient-to-br from-[#F0F7FF] to-white rounded-2xl p-8 border border-[#2563EB]/10'
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.4 }}
    >
      <form onSubmit={handleSubmit} className='space-y-6'>
        <p className='text-gray-800 text-lg mb-6 text-center font-[500]'>
          Final step: Add your email to get your results delivered!
        </p>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className='p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm'
          >
            {error}
          </motion.div>
        )}
        <motion.div whileHover={!isLoading ? { scale: 1.01 } : {}} whileTap={!isLoading ? { scale: 0.99 } : {}}>
          <input
            type='email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder='Enter your email address'
            disabled={isLoading}
            className='w-full p-4 rounded-xl border-2 border-[#F0F7FF] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 bg-white transition-all duration-200 text-lg disabled:opacity-50 disabled:cursor-not-allowed'
            required
          />
        </motion.div>
        <p className='text-gray-600 text-sm mb-4 text-center' >
        We'll send you short English lessons focused on idioms. No spam ever. 
        </p>
        <motion.button
          type='submit'
          disabled={isLoading}
          className='w-full p-4 rounded-xl bg-[#2563EB] text-white font-semibold transition-all duration-200 shadow-[0_4px_16px_0_rgba(37,99,235,0.2)] text-lg disabled:opacity-50 disabled:cursor-not-allowed'
          whileHover={!isLoading ? { scale: 1.01, backgroundColor: '#1E40AF' } : {}}
          whileTap={!isLoading ? { scale: 0.98 } : {}}
        >
          {isLoading ? 'Submitting...' : 'Submit'}
        </motion.button>
      </form>
    </motion.div>
  )
}

const FinalStep = ({ score }) => {
  // const [error, setError] = useState(null)

  // Define level thresholds and descriptions
  const getLevelInfo = (score) => {
    if (score === 25) {
      return {
        level: 'C2',
        description:
          'You have demonstrated complete mastery of idiomatic expressions and colloquialisms.',
        status: 'accepted',
        message: "Congratulations! You're more than qualified for SpeakDuo events.",
        link: 'https://speakduo.com/live'
      }
    } else if (score >= 22) {
      return {
        level: 'C1',
        description:
          'You can understand and use a wide range of idiomatic expressions and colloquialisms fluently.',
        status: 'accepted',
        message: "Excellent! You're perfectly qualified for SpeakDuo events.",
        link: 'https://speakduo.com/live'
      }
    } else if (score >= 19) {
      return {
        level: 'B2+',
        description: 'You can use idiomatic language effectively in familiar contexts.',
        status: 'accepted',
        message: "Great job! You're qualified to join SpeakDuo events.",
        link: 'https://speakduo.com/live'
      }
    } else if (score >= 15) {
      return {
        level: 'B2',
        description: 'You understand and can use common idioms well.',
        status: 'accepted',
        message: 'Well done! You meet the requirements for SpeakDuo events.',
        link: 'https://speakduo.com/live'
      }
    } else if (score >= 13) {
      return {
        level: 'B1',
        description: 'You understand basic idioms in familiar contexts.',
        status: 'borderline',
        message:
          "You're very close! With a bit more practice, you'll be ready for SpeakDuo events.",
        link: 'https://speakduo.com/live'
      }
    } else if (score >= 8) {
      return {
        level: 'A2',
        description: 'You have limited recognition of simple idioms.',
        status: 'comeback',
        message: 'Keep learning! We recommend practicing for 3 months before trying again.',
      }
    } else {
      return {
        level: 'A1',
        description: "You're just beginning to learn idiomatic expressions.",
        status: 'comeback',
        message: 'Focus on building your foundation. Come back in 3 months!',
      }
    }
  }

  const levelInfo = getLevelInfo(score)
  const isAccepted = levelInfo.status === 'accepted'
  const isBorderline = levelInfo.status === 'borderline'
  const shouldShowSocialCTA = !isAccepted // Show for both borderline and comeback status

  useEffect(() => {
    if (isAccepted) {
      // Trigger confetti animation
      const duration = 3000
      const animationEnd = Date.now() + duration
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 }

      function randomInRange(min, max) {
        return Math.random() * (max - min) + min
      }

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now()

        if (timeLeft <= 0) {
          return clearInterval(interval)
        }

        const particleCount = 50 * (timeLeft / duration)

        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        })
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        })
      }, 250)

      return () => clearInterval(interval)
    }
  }, [isAccepted])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className='relative max-w-2xl mx-auto'
    >
      {/* Decorative elements */}
      <div className='absolute -top-20 -left-20 w-40 h-40 bg-[#F0F7FF] rounded-full blur-3xl opacity-30'></div>
      <div className='absolute -bottom-20 -right-20 w-40 h-40 bg-[#F0F7FF] rounded-full blur-3xl opacity-30'></div>

      <div className='relative backdrop-blur-sm bg-white/80 rounded-3xl shadow-[0_8px_32px_0_rgba(31,38,135,0.1)] border border-white/30 p-8 overflow-hidden'>
        <motion.div
          className='relative z-10 text-center'
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className='w-32 h-32 bg-gradient-to-br from-[#2563EB] to-[#1E40AF] rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_8px_32px_0_rgba(37,99,235,0.2)]'
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', duration: 0.8 }}
          >
            <span className='text-4xl text-white font-bold'>{score}/25</span>
          </motion.div>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className='mb-4'
          >
            <h2 className='text-3xl font-bold text-gray-900 mb-2'>
              Level {levelInfo.level} {isAccepted ? '🎉' : isBorderline ? '📈' : '📚'}
            </h2>
            <p className='text-lg text-[#2563EB] font-medium'>{levelInfo.description}</p>
          </motion.div>
          <motion.p
            className='text-gray-600 text-lg mb-8'
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {levelInfo.link ? (
              <>
                {levelInfo.message.replace('SpeakDuo events.', '')}
                <a
                  href={levelInfo.link}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-[#2563EB] hover:text-[#1E40AF] underline font-medium'
                >
                  SpeakDuo events
                </a>
                .
              </>
            ) : (
              levelInfo.message
            )}
          </motion.p>

          {shouldShowSocialCTA && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className='bg-gradient-to-br from-[#F0F7FF] to-white rounded-2xl p-6 mb-8 border border-[#2563EB]/10'
            >
              <h3 className='text-xl font-semibold text-gray-800 mb-4'>
                Want to improve faster? 🚀
              </h3>
              <div className='space-y-4'>
                <p className='text-gray-600'>Follow us for daily English tips:</p>
                <div className='flex justify-center space-x-4'>
                  <a
                    href='https://youtube.com/@SpeakDuo'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex items-center px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors'
                  >
                    <span className='mr-2'>📺</span> YouTube
                  </a>
                  <a
                    href='https://tiktok.com/@SpeakDuo'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex items-center px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors'
                  >
                    <span className='mr-2'>🎵</span> TikTok
                  </a>
                </div>
              </div>
            </motion.div>
          )}

          {/* {isAccepted ? (
            <motion.div 
              className="space-y-6"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="bg-gradient-to-br from-[#F0F7FF] to-white rounded-2xl p-8 border border-[#2563EB]/10">
                <p className="text-gray-800 text-lg">
                  Great news! You're qualified to participate in SpeakDuo's events. 
                  We'll be in touch with exclusive invitations soon!
                </p>
              </div>
              
              <motion.div 
                className="bg-white rounded-2xl p-8 border-2 border-[#2563EB] shadow-[0_8px_32px_0_rgba(37,99,235,0.1)]"
                whileHover={{ scale: 1.01 }}
                transition={{ type: "spring", stiffness: 300, damping: 17 }}
              >
                <div className="bg-[#F0F7FF] rounded-xl p-3 inline-block mb-4">
                  <span className="text-2xl">🎁</span>
                </div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-3">
                  Special Offer
                </h3>
                <p className="text-gray-600 text-lg mb-8">
                  Claim your 5 free conversation practice sessions with native speakers!
                </p>
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        className="w-full p-4 rounded-xl border-2 border-[#F0F7FF] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 bg-white transition-all duration-200 text-lg"
                        required
                      />
                    </motion.div>
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full p-4 rounded-xl bg-[#2563EB] text-white font-semibold transition-all duration-200 shadow-[0_4px_16px_0_rgba(37,99,235,0.2)] text-lg
                        ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#1E40AF]'}`}
                      whileHover={!isSubmitting ? { scale: 1.01 } : {}}
                      whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    >
                      {isSubmitting ? 'Submitting...' : 'Claim Your Free Sessions'}
                    </motion.button>
                  </form>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-[#2563EB] font-semibold text-lg"
                  >
                    Thanks! Check your email for details about your free sessions.
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          ) : (
            <motion.div 
              className="bg-gradient-to-br from-[#F0F7FF] to-white rounded-2xl p-8 border border-[#2563EB]/10"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <p className="text-gray-800 text-lg mb-6">
                    {isBorderline 
                      ? "Want to improve your English and qualify for SpeakDuo events? Get personalized practice materials!"
                      : "Want to boost your English skills? Get our 3-month improvement plan and learning materials!"}
                  </p>
                  <motion.div
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full p-4 rounded-xl border-2 border-[#F0F7FF] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 bg-white transition-all duration-200 text-lg"
                      required
                    />
                  </motion.div>
                  <motion.button
                    type="submit"
                    className="w-full p-4 rounded-xl bg-[#2563EB] text-white font-semibold hover:bg-[#1E40AF] transition-all duration-200 shadow-[0_4px_16px_0_rgba(37,99,235,0.2)] text-lg"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Get Learning Materials
                  </motion.button>
                </form>
              ) : (
                <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-gray-800 text-lg"
                >
                  Thanks! Check your inbox for our learning materials and improvement plan.
                </motion.p>
              )}
            </motion.div>
          )} */}
        </motion.div>
      </div>

      {/* Disclaimer */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className='text-center text-gray-400 text-sm mt-6 px-4'
      >
        Note: This test provides an estimated evaluation of your English proficiency and should not
        be considered an official certification. Results are approximate and meant for guidance
        purposes only.
      </motion.p>

      {/* {error && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className='text-red-500 text-sm mt-2'
        >
          {error}
        </motion.div>
      )} */}
    </motion.div>
  )
}

export default Results
